import { useState, useRef, useEffect } from 'react';
import styles from '../form-submissions.module.css';
import { Upload, FileText, Trash2, Download, FolderOpen, Loader } from 'lucide-react';

// Storage key prefix for documents
const STORAGE_KEY = 'arka_documents';

export interface UploadedDocument {
  id: string;
  name: string;
  size: number;
  uploadedAt: string;
  type: string;
  url?: string;
  base64?: string; // Store base64 for persistence
}

interface DocumentsSectionProps {
  submissionId?: string; // To store documents per submission
  documents?: UploadedDocument[];
  onDocumentsChange?: (documents: UploadedDocument[]) => void;
  readOnly?: boolean;
}

// Helper to convert File to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

// Helper to get storage key
const getStorageKey = (submissionId?: string) => {
  return submissionId ? `${STORAGE_KEY}_${submissionId}` : STORAGE_KEY;
};

export function DocumentsSection({
  submissionId,
  documents = [],
  onDocumentsChange,
  readOnly = false,
}: DocumentsSectionProps) {
  const [localDocuments, setLocalDocuments] = useState<UploadedDocument[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isOpeningPicker, setIsOpeningPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load documents from localStorage on mount
  useEffect(() => {
    const storageKey = getStorageKey(submissionId);
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const parsedDocs: UploadedDocument[] = JSON.parse(stored);
        // Restore blob URLs from base64
        const restoredDocs = parsedDocs.map(doc => ({
          ...doc,
          url: doc.base64 || doc.url,
        }));
        setLocalDocuments(restoredDocs);
      } catch (e) {
        console.error('Error loading documents from storage:', e);
        setLocalDocuments(documents);
      }
    } else {
      setLocalDocuments(documents);
    }
    setIsLoading(false);
  }, [submissionId, documents]);

  // Save documents to localStorage whenever they change
  const saveToStorage = (docs: UploadedDocument[]) => {
    const storageKey = getStorageKey(submissionId);
    try {
      localStorage.setItem(storageKey, JSON.stringify(docs));
    } catch (e) {
      console.error('Error saving documents to storage:', e);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleUploadClick = () => {
    setIsOpeningPicker(true);
    fileInputRef.current?.click();
  };

  // Stop loading as soon as file picker opens (input gets clicked)
  const handleInputClick = () => {
    // File picker is now open, stop loading
    setTimeout(() => {
      setIsOpeningPicker(false);
    }, 100);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      handleFiles(files);
    }
    // Reset input value to allow selecting same file again
    e.target.value = '';
  };

  const handleFiles = async (files: File[]) => {
    // Convert files to base64 for storage persistence
    const newDocsPromises = files.map(async file => {
      const base64 = await fileToBase64(file);
      return {
        id: `doc-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        type: file.type,
        url: base64, // Use base64 as URL (works for download)
        base64: base64,
      };
    });

    const newDocs = await Promise.all(newDocsPromises);
    const updatedDocs = [...localDocuments, ...newDocs];

    setLocalDocuments(updatedDocs);
    saveToStorage(updatedDocs);
    onDocumentsChange?.(updatedDocs);
  };

  const handleDelete = (docId: string) => {
    const updatedDocs = localDocuments.filter(d => d.id !== docId);
    setLocalDocuments(updatedDocs);
    saveToStorage(updatedDocs);
    onDocumentsChange?.(updatedDocs);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const totalDocs = localDocuments.length;

  if (isLoading) {
    return (
      <div className={styles.documentsSection}>
        <div className={styles.documentsSectionHeader}>
          <div className={styles.documentsSectionTitle}>
            <FolderOpen size={20} className={styles.textOrange} />
            <span>Supporting Documents</span>
          </div>
        </div>
        <div style={{ padding: '32px', display: 'flex', justifyContent: 'center' }}>
          <Loader size={24} className={styles.uploadSpinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.documentsSection}>
      {/* Header */}
      <div className={styles.documentsSectionHeader}>
        <div className={styles.documentsSectionTitle}>
          <FolderOpen size={20} className={styles.textOrange} />
          <span>Supporting Documents</span>
        </div>
        <span className={styles.documentsCount}>
          {totalDocs} {totalDocs === 1 ? 'document' : 'documents'}
        </span>
      </div>

      {/* Upload Area (only if not readOnly) */}
      {!readOnly && (
        <div
          className={`${styles.uploadDropZone} ${isDragging ? styles.uploadDropZoneDragging : ''} ${isOpeningPicker ? styles.uploadDropZoneLoading : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={!isOpeningPicker ? handleUploadClick : undefined}
          style={{ cursor: isOpeningPicker ? 'wait' : 'pointer' }}
        >
          {isOpeningPicker ? (
            <>
              <Loader size={32} className={styles.uploadSpinner} />
              <div className={styles.uploadText}>
                <strong>Opening file picker...</strong>
              </div>
            </>
          ) : (
            <>
              <Upload size={32} className={styles.uploadIcon} />
              <div className={styles.uploadText}>
                <strong>Drop files here or click to upload</strong>
                <span>PDF, DOC, JPG, PNG up to 10MB each</span>
              </div>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            onClick={handleInputClick}
            style={{ display: 'none' }}
          />
        </div>
      )}

      {/* Documents List */}
      {totalDocs > 0 && (
        <div className={styles.documentsList}>
          {localDocuments.map(doc => (
            <div key={doc.id} className={styles.documentItem}>
              <div className={styles.documentItemIcon}>
                <FileText size={18} />
              </div>
              <div className={styles.documentItemInfo}>
                <span className={styles.documentItemName}>{doc.name}</span>
                <span className={styles.documentItemMeta}>
                  {formatFileSize(doc.size)} • {new Date(doc.uploadedAt).toLocaleDateString()}
                </span>
              </div>
              <div className={styles.documentItemActions}>
                {doc.url && (
                  <a
                    href={doc.url}
                    download={doc.name}
                    className={styles.documentActionBtn}
                    title="Download"
                  >
                    <Download size={16} />
                  </a>
                )}
                {!readOnly && (
                  <button
                    className={`${styles.documentActionBtn} ${styles.documentActionBtnDelete}`}
                    onClick={() => handleDelete(doc.id)}
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
